import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import './App.css';
import {
  MapPin, MessageCircle, ArrowDown, Heart, Sparkles,
  Gift, Shirt, Ticket, Volume2, VolumeX, Calendar
} from 'lucide-react';
import { listaInvitados } from './data/invitados';
import fotoHero from './image/mostrando_anillo.jpeg';
import fotoDivisor from './image/besa_mano.png';
import galeria1 from './image/alreversa.png';
import galeria2 from './image/felicidad.jpeg';
import galeria3 from './image/romantico.jpeg';
import musicaFondo from './music/DIOS TE PUSO EN MI CAMINO  CANCION CRISTIANA ROMANTICA - Frank Anthony (youtube).mp3';

const weddingData = {
  groom: "David",
  bride: "Jenny",
  date: "2026-10-17T14:30:00",
  locationName: "Finca & Jardín \"El Rosario\"",
  locationAddress: "Estacion Joaquin, Gto.",
  locationUrl: "https://maps.app.goo.gl/mZriEVfEAU7D1Pzu5",
  phones: { groom: "524296977743", bride: "524621926526" },
  images: {
    hero: fotoHero,
    divider: fotoDivisor,
    gallery: [galeria1, galeria2, galeria3],
  },
  dressCode: "Libre"
};

const MESES = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];

const formatDayBadge = (dateStr) => {
  const d = new Date(dateStr);
  return { day: d.getDate(), monthYear: `${MESES[d.getMonth()]} ${d.getFullYear()}` };
};

const formatTime = (dateStr) => {
  const d = new Date(dateStr);
  let h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
};

/* Pequeño divisor ornamental inspirado en el moodboard de cuarzo
   facetado: el motivo distintivo que recorre toda la invitación. */
const FacetDivider = ({ light = false }) => (
  <div className={`facet-divider ${light ? 'facet-divider--light' : ''}`}>
    <span className="facet-line" />
    <span className="facet-gem" />
    <span className="facet-gem facet-gem--lg" />
    <span className="facet-gem" />
    <span className="facet-line" />
  </div>
);

const Countdown = ({ targetDate }) => {
  const calculateTimeLeft = () => {
    const difference = +new Date(targetDate) - +new Date();
    if (difference <= 0) return { dias: 0, horas: 0, min: 0, seg: 0 };
    return {
      dias: Math.floor(difference / (1000 * 60 * 60 * 24)),
      horas: Math.floor((difference / (1000 * 60 * 60)) % 24),
      min: Math.floor((difference / 1000 / 60) % 60),
      seg: Math.floor((difference / 1000) % 60),
    };
  };
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());
  useEffect(() => {
    const timer = setTimeout(() => setTimeLeft(calculateTimeLeft()), 1000);
    return () => clearTimeout(timer);
  });
  const timerComponents = Object.keys(timeLeft).map((interval) => {
    if (!timeLeft[interval] && timeLeft[interval] !== 0) return null;
    return (
      <div className="time-item" key={interval}>
        <span className="time-val">{timeLeft[interval]}</span>
        <span className="time-label">{interval}</span>
      </div>
    );
  });
  return (
    <div className="countdown-wrapper">
      {timerComponents.length ? timerComponents : <span className="countdown-today">¡Es hoy!</span>}
    </div>
  );
};

function Invitation() {
  const { id } = useParams();
  const [invitado, setInvitado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    audioRef.current = new Audio(musicaFondo);
    audioRef.current.loop = true;
    audioRef.current.volume = 0.5;
    const playPromise = audioRef.current.play();
    if (playPromise !== undefined) {
      playPromise.then(() => {
        setIsPlaying(true);
      }).catch(() => {
        setIsPlaying(false);
      });
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const toggleMusic = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const [autoScroll, setAutoScroll] = useState(true);
  useEffect(() => {
    if (loading || !autoScroll) return;
    const el = contentRef.current;
    const scrollInterval = setInterval(() => {
      if (el) {
        el.scrollBy(0, 1);
        if (el.scrollTop + el.clientHeight >= el.scrollHeight) setAutoScroll(false);
      } else {
        window.scrollBy(0, 1);
        if (window.innerHeight + window.scrollY >= document.body.offsetHeight) setAutoScroll(false);
      }
    }, 40);
    return () => clearInterval(scrollInterval);
  }, [loading, autoScroll]);

  useEffect(() => {
    const stopScrolling = () => {
      if (autoScroll) setAutoScroll(false);
    };
    window.addEventListener("wheel", stopScrolling);
    window.addEventListener("touchmove", stopScrolling);
    window.addEventListener("keydown", stopScrolling);
    window.addEventListener("mousedown", stopScrolling);
    return () => {
      window.removeEventListener("wheel", stopScrolling);
      window.removeEventListener("touchmove", stopScrolling);
      window.removeEventListener("keydown", stopScrolling);
      window.removeEventListener("mousedown", stopScrolling);
    };
  }, [autoScroll]);

  // Revela cada sección con un fundido suave al entrar en pantalla.
  useEffect(() => {
    if (loading) return;
    const els = document.querySelectorAll('.reveal');
    if (!els.length) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [loading]);

  const addToCalendar = () => {
    const title = encodeURIComponent(`Boda ${weddingData.bride} & ${weddingData.groom} 💍`);
    const details = encodeURIComponent("¡Nos casamos! Gracias por acompañarnos.");
    const location = encodeURIComponent(`${weddingData.locationName}, ${weddingData.locationAddress}`);
    const start = "20261017T143000";
    const end = "20261017T230000";
    const url = `https://www.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&details=${details}&location=${location}&ctz=America/Mexico_City`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    if (id && listaInvitados[id]) {
      setInvitado(listaInvitados[id]);
    }
    const timer = setTimeout(() => {
      setLoading(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, [id]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <svg viewBox="0 0 100 100" className="loading-heart-svg" aria-hidden="true">
            {/* Mitad izquierda: se traza del centro-arriba hacia la punta */}
            <path pathLength="1"
              d="M 50,32 C 45,20 24,8 10,28 C 0,44 10,64 50,84"
              className="heart-half" />
            {/* Mitad derecha: se traza simultáneamente del centro-arriba hacia la punta */}
            <path pathLength="1"
              d="M 50,32 C 55,20 76,8 90,28 C 100,44 90,64 50,84"
              className="heart-half" />
            {/* Corazón completo relleno — aparece cuando los trazos terminan */}
            <path
              d="M 50,32 C 45,20 24,8 10,28 C 0,44 10,64 50,84 C 90,64 100,44 90,28 C 76,8 55,20 50,32 Z"
              className="heart-fill" />
          </svg>
          <h2 className="loading-names">{weddingData.groom} & {weddingData.bride}</h2>
          <p className="loading-tagline">Preparando algo hermoso...</p>
        </div>
      </div>
    );
  }

  const welcomeMessage = invitado?.mensaje ||
    "Será un día lleno de amor, gratitud y momentos inolvidables, y nos encantará contar con tu presencia para celebrarlo juntos.";

  const { day, monthYear } = formatDayBadge(weddingData.date);

  return (
    <div className="layout">
      <div
        className="mobile-bg"
        style={{ backgroundImage: `url(${weddingData.images.hero})` }}
      />

      {/* Imagen lateral sticky (desktop) / hero normal (móvil) */}
      <div className="side-image-container">
        <img src={weddingData.images.hero} alt="Pareja" className="side-image" />
        <div className="overlay-mobile">
          <h1 className="mobile-title">{weddingData.groom} & {weddingData.bride}</h1>
        </div>
      </div>

      <div className="content-container" ref={contentRef}>

        {/* Header */}
        <section className="section header-section reveal">
          <div className="sparkles-container">
            <Sparkles className="sparkle sparkle-1" size={18} />
            <Sparkles className="sparkle sparkle-2" size={14} />
          </div>
          <p className="overline">Con la bendición de Dios y de nuestras familias</p>
          <h1 className="desktop-title">
            {weddingData.groom} <span className="amp">&</span> {weddingData.bride}
          </h1>
          <Heart className="heart-icon" size={20} fill="#244667" stroke="#244667" />
          <p className="verse">
            Tenemos el honor de invitarte a <strong>nuestra boda</strong>.
          </p>
          <FacetDivider />
          <p className="scripture-quote">
            "No me ruegues que te deje y me aparte de ti; <br />
            porque a dondequiera que tú vayas, iré yo, <br />
            y dondequiera que tú vivas, viviré. Tu pueblo será mi pueblo, <br />
            y tu Dios será mi Dios"<br />
            <span className="scripture-source">— Rut 1:16 —</span>
          </p>
          <ArrowDown className="scroll-icon" size={20} />
        </section>

        {/* Fecha */}
        <section className="section date-section reveal">
          <div className="celebration-banner">
            <Heart className="floating-heart heart-left" size={16} fill="#D8E8F2" stroke="#D8E8F2" />
            <div className="date-badge">
              <span className="day-number">{day}</span>
              <span className="month-year">{monthYear}</span>
            </div>
            <Heart className="floating-heart heart-right" size={16} fill="#D8E8F2" stroke="#D8E8F2" />
          </div>
          <p className="time-text">{formatTime(weddingData.date)}</p>
          <Countdown targetDate={weddingData.date} />
          <button onClick={addToCalendar} className="btn-outline btn-calendar">
            <Calendar size={16} /> Agregar al calendario
          </button>
        </section>

        {/* Foto divisor */}
        <div className="image-divider">
          <img
            src={weddingData.images.divider}
            alt="Pareja"
            className="image-divider-img"
          />
        </div>

        {/* Boleto personalizado */}
        <section className="section welcome-msg-section reveal">
          {invitado && (
            <div className="ticket-card">
              <p className="ticket-label">Invitación especial para</p>
              <h2 className="ticket-name">{invitado.nombre}</h2>
              <div className="ticket-passes">
                <Ticket size={18} />
                <span>
                  {invitado.pases} {invitado.pases === 1 ? 'Lugar' : 'Lugares'} Reservados
                </span>
              </div>
              {invitado.ninos > 0 && (
                <p className="ticket-children-note">
                  ({invitado.adultos} Adultos / {invitado.ninos} {invitado.ninos === 1 ? 'Niño' : 'Niños'})
                </p>
              )}
            </div>
          )}
          <p className="welcome-text">"{welcomeMessage}"</p>
        </section>

        {/* Galería de fotos */}
        <section className="section gallery-section reveal">
          <p className="overline">Nuestros momentos</p>
          <div className="gallery-scroll-container">
            {weddingData.images.gallery.map((src, i) => (
              <img key={i} src={src} alt={`Foto ${i + 1}`} className="gallery-item" />
            ))}
          </div>
        </section>

        {/* Ubicación y Dress code */}
        <section className="section location-section reveal">
          <div className="icon-badge">
            <MapPin size={22} />
          </div>
          <h3>Ceremonia & Recepción</h3>
          <p className="venue-name">{weddingData.locationName}</p>
          <p className="venue-address">{weddingData.locationAddress}</p>
          <a href={weddingData.locationUrl} target="_blank" rel="noreferrer" className="btn-outline">
            Ver mapa
          </a>
          <div className="dress-code-container">
            <div className="divider-small" />
            <div className="icon-badge">
              <Shirt size={22} />
            </div>
            <p className="dress-code-label">Código de vestimenta</p>
            <p className="dress-code-value">{weddingData.dressCode}</p>
          </div>
        </section>

        {/* Regalo */}
        <section className="section gift-section reveal">
          <div className="icon-badge">
            <Gift size={22} />
          </div>
          <h3>Obsequios</h3>
          <p className="gift-text">Tu presencia es lo más importante para nosotros.</p>
          <p className="gift-text-small">
            Si deseas obsequiarnos algo más para celebrar este día, <br />
            agradecemos infinitamente tu aportación en un sobre.
          </p>
        </section>

        {/* RSVP */}
        <section className="section rsvp-section reveal">
          <FacetDivider light />
          <h3>Confirmación</h3>
          <div className="rsvp-intro-wrap">
            <p className="rsvp-intro">
              Estamos preparando cada detalle con mucho amor.<br />
              <strong>Tu lugar ha sido reservado especialmente para ti.</strong>
            </p>
            <p className="rsvp-deadline">
              Por favor, ayúdanos confirmando tu asistencia antes del <strong>01 de Octubre</strong> para asegurar tu espacio en nuestra celebración.
            </p>
          </div>
          <div className="rsvp-buttons">
            {(!invitado || invitado.host === 'david') && (
              <a
                href={`https://wa.me/${weddingData.phones.groom}?text=${encodeURIComponent(
                  `Hola David 👋, soy ${invitado ? invitado.nombre : ''}.\n\n` +
                  `Muchas gracias por la invitación. ✨\n` +
                  `✅ Confirmo con gusto mi asistencia y los ${invitado ? invitado.pases : ''} lugares que han reservado para mí.\n\n` +
                  `¡Ahí nos vemos para celebrar!`
                )}`}
                className="btn-rsvp" target="_blank" rel="noreferrer"
              >
                <MessageCircle size={16} /> Confirmar con {weddingData.groom}
              </a>
            )}
            {(!invitado || invitado.host === 'jenny') && (
              <a
                href={`https://wa.me/${weddingData.phones.bride}?text=${encodeURIComponent(
                  `Hola Jenny 👋, soy ${invitado ? invitado.nombre : ''}.\n\n` +
                  `Muchas gracias por la invitación. ✨\n` +
                  `✅ Confirmo con gusto mi asistencia y los ${invitado ? invitado.pases : ''} lugares que han reservado para mí.\n\n` +
                  `¡Ahí nos vemos para celebrar!`
                )}`}
                className="btn-rsvp" target="_blank" rel="noreferrer"
              >
                <MessageCircle size={16} /> Confirmar con {weddingData.bride}
              </a>
            )}
          </div>
          <p className="exclusive-note">Nos alegrará contar con tu presencia.</p>
        </section>

        <footer className="footer-simple">
          <Heart className="footer-heart" size={14} fill="#42678B" stroke="#42678B" />
          <small>Con amor, {weddingData.groom} y {weddingData.bride}.</small>
          <Heart className="footer-heart" size={14} fill="#42678B" stroke="#42678B" />
        </footer>
      </div>

      {/* Botón música */}
      <button
        onClick={toggleMusic}
        className="music-btn-fixed"
        aria-label={isPlaying ? 'Pausar música' : 'Reproducir música'}
      >
        {isPlaying ? (
          <span className="music-icon-wrap">
            <Volume2 size={22} color="#244667" />
            <span className="ripple" />
          </span>
        ) : (
          <VolumeX size={22} color="#9AAAB7" />
        )}
      </button>
    </div>
  );
}

export default Invitation;